import type {AccountType} from "@hosttools/core/constant";
import {channels} from "@hosttools/core/constant";
import {eventIdAccountAdded} from "@hosttools/core/shared/facebookEvents.types";
import {Modal, VStack} from "@hosttools/frontend";
import {useMobileView} from "@hosttools/frontend/hooks/useMobileView";
import type {ChannelObject} from "@hosttools/frontend/models/channel";
import {buildChannel} from "@hosttools/frontend/models/channel";
import type {Endpoints} from "@hosttools/web/server/routes/routes.channels.types";
import type React from "react";
import {memo, useCallback, useContext, useEffect, useMemo, useState} from "react";
import TagManager from "react-gtm-module";

import {UserContext} from "../../providers/UserProvider";

import type {ConnectedChannels} from "./Channels";
import Channels from "./Channels";
import Code from "./Code";
import Credentials from "./Credentials";
import type {CredentialsType, Errors, Method} from "./types";
import Verification from "./Verification";

import {useAirbnbOAuthUrl} from "@/client/hooks/useAirbnbOAuthUrl";
import {trackFacebookEvent} from "@/client/hooks/useFacebookPixel";

export type Props = {
    channel?: AccountType;
    username?: string;
    accountID?: string;
    hideCloseButtons?: boolean;
    hideSocial?: boolean;
    oAuthReturnUrl?: string;
    oAuthStateParameter?: string;
    responsive?: boolean;
    modalSize?: "xl" | "md";
    onHide: (isSuccess?: boolean | undefined) => void;
    onConnected?: (accountID: string) => void;
};

type TypeName = "credentials" | "channels" | "verification" | "code" | "success";

type DataResponse = {
    type?: TypeName;
    session?: any;
    error?: string;
    methods: Method[];
    challengeTicket: string;
    info?: string;
    errorCode?: string;
    accountID?: string;
};

const ModalAddAccount: React.FC<Props> = props => {
    const {
        channel: initChannel,
        username = "",
        hideCloseButtons = false,
        hideSocial,
        accountID = "",
        oAuthReturnUrl,
        oAuthStateParameter,
        responsive = true,
        modalSize = "md",
        onHide,
        onConnected
    } = props;
    const isMobile = useMobileView();
    const channelObject = useMemo(
        () => (initChannel ? buildChannel(initChannel) : undefined),
        [initChannel]
    );
    const {accounts, updateUser, user} = useContext(UserContext);
    const [channel, setChannel] = useState<Exclude<ChannelObject, {value: "internal"}> | undefined>(
        channelObject
    );
    const [credentials, setCredentials] = useState<CredentialsType | undefined>(
        channelObject
            ? {
                  channel: channelObject.value,
                  username: username || "",
                  password: "",
                  apiKey: "",
                  propertyID: ""
              }
            : undefined
    );
    const [verificationIndex, setVerificationIndex] = useState(0);
    const [methods, setMethods] = useState<Method[]>([]);
    const [challengeTicket, setChallengeTicket] = useState<string>();
    const [code, setCode] = useState<string | undefined>();
    const [connectedChannels, setConnectedChannels] = useState<ConnectedChannels>();
    const [disconnectedChannels, setDisconnectedChannels] = useState<ConnectedChannels>();
    const [session, setSession] = useState({});
    const [type, setType] = useState<TypeName | undefined>(
        channelObject ? "credentials" : "channels"
    );
    const [errors, setErrors] = useState<Errors>({});
    const [info, setInfo] = useState<string>("");
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [buttonText, setButtonText] = useState("");
    const [title, setTitle] = useState<string>("");

    useEffect(() => {
        if (type === "credentials") {
            setButtonText(`Connect with ${channel?.label}`);
            setTitle(`Connect ${channel?.label} Account`);
        } else if (type === "verification") {
            setButtonText("Send Verification");
            setTitle(`Verify ${channel?.label} Account`);
        } else if (type === "code") {
            setButtonText("Submit Code");
            setTitle(`Verify ${channel?.label} Account`);
        } else if (type === "channels") {
            setButtonText("Continue");
            setTitle("What account would you like help with host tools?");
        } else {
            setButtonText("Continue");
            setTitle("Connect Account");
        }
    }, [channel, type]);

    useEffect(() => {
        setConnectedChannels(
            accounts.reduce((connectedChannels, account) => {
                if (account.lastLoginAttemptSuccessful) {
                    connectedChannels[account.type] = (connectedChannels[account.type] ?? 0) + 1;
                }
                return connectedChannels;
            }, {} as ConnectedChannels)
        );
        setDisconnectedChannels(
            accounts.reduce((connectedChannels, account) => {
                if (account.lastLoginAttemptSuccessful === false) {
                    connectedChannels[account.type] = (connectedChannels[account.type] ?? 0) + 1;
                }
                return connectedChannels;
            }, {} as ConnectedChannels)
        );
    }, [accounts]);

    const hideAddAccount = useCallback(
        async (isSuccess: boolean, accountID: string) => {
            if (isSuccess) {
                const tagManagerArgs = {
                    dataLayer: {
                        event: `${channel?.label} Added Event`,
                        category: channel?.value?.toLowerCase(),
                        action: "account",
                        label: "added",
                        value: 0
                    }
                };
                TagManager.dataLayer(tagManagerArgs);

                // Track account addition using deterministic event ID
                if (accountID && user._id) {
                    const eventId = eventIdAccountAdded(user._id, accountID);
                    trackFacebookEvent(
                        "accountAdded",
                        {
                            source: "modal_add_account"
                        },
                        eventId
                    );
                }
            }
            onHide(isSuccess);
        },
        [channel, onHide, user._id]
    );

    const handleResponse = useCallback(
        (data: DataResponse) => {
            const {type, session, error, methods, info, errorCode, challengeTicket} = data;
            setSession(session);
            setErrors({error: error || "", errorCode: errorCode || ""});
            setInfo(info || "");

            if (type === "success") {
                // Use data.accountID from server response for new account creations
                // or fall back to prop accountID for existing account operations
                const responseAccountID = data.accountID || accountID;
                if (responseAccountID) {
                    hideAddAccount(true, responseAccountID);
                    return;
                }
                setType("channels");
                return;
            }

            if (methods) {
                setMethods(methods);
            }
            if (challengeTicket) {
                setChallengeTicket(challengeTicket);
            }
            setType(type);
        },
        [accountID, hideAddAccount]
    );

    const selectChannel = useCallback((channel: AccountType) => {
        const obj = buildChannel(channel);
        setChannel(obj);
        setErrors({});
        setType("credentials");
    }, []);

    const handleChangeCredentials = useCallback((newCredentials: CredentialsType) => {
        setCredentials(credentials => {
            return {...credentials, ...newCredentials};
        });
    }, []);

    const validateCredentials = useCallback(() => {
        const errors: Errors = {};
        let formIsValid = true;
        const {value, label} = channel ?? {};
        if (value === "Airbnb" || value === "HomeAway" || value === "August") {
            if (!credentials?.username) {
                formIsValid = false;
                errors.username = `Please enter your ${label} username.`;
            } else {
                const re = /\S+@\S+\.\S+/;
                const validEmail = re.test(credentials.username);
                if (!validEmail) {
                    formIsValid = false;
                    errors.username = "Please enter a valid email address.";
                }
            }
            if (!credentials?.password || credentials.password.length === 0) {
                formIsValid = false;
                errors.password = `Please enter your ${label} password.`;
            }
        } else if (value === "Houfy") {
            if (!credentials?.apiKey || credentials.apiKey.length === 0) {
                formIsValid = false;
                errors.apiKey = `Please enter your ${label} API Key.`;
            }
        } else if (!credentials?.propertyID || credentials.propertyID.length === 0) {
            formIsValid = false;
            errors.propertyID = `Please enter your ${label} Property ID.`;
        }
        setErrors(errors);
        return formIsValid;
    }, [channel, credentials]);

    const submitAddAccount = useCallback(async () => {
        if (showSpinner) {
            return;
        }
        if (validateCredentials() && channel && credentials) {
            setShowSpinner(true);
            setErrors({});
            const url = "/addAccount";
            let body: Endpoints["/addAccount"][1];
            if (
                (credentials.channel === "HomeAway" || credentials.channel === "August") &&
                credentials.username &&
                credentials.password
            ) {
                body = {
                    channel: credentials.channel,
                    credentials: {
                        username: credentials.username,
                        password: credentials.password
                    }
                };
            } else if (credentials.channel === "Booking" && credentials.propertyID) {
                body = {
                    channel: credentials.channel,
                    credentials: {
                        propertyID: credentials.propertyID
                    }
                };
            } else if (credentials.channel === "Houfy" && credentials.apiKey) {
                body = {
                    channel: credentials.channel,
                    credentials: {
                        apiKey: credentials.apiKey
                    }
                };
            } else {
                throw new Error("There was an issue adding account.");
            }
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json"
                    },
                    // use label for data sounds nonsense
                    body: JSON.stringify(body)
                });

                if (response.ok) {
                    const data = await response.json();
                    handleResponse(data);
                    onConnected?.(data.accountID);
                } else {
                    const data = await response.json();
                    const errors = {error: data.message};
                    setErrors(errors);
                }
            } catch {
                setErrors({error: "There was an issue adding account."});
            } finally {
                setShowSpinner(false);
            }
        }
    }, [channel, credentials, showSpinner, handleResponse, validateCredentials, onConnected]);

    const validateVerificationIndex = useCallback(() => {
        const errors: Errors = {};
        let formIsValid = true;
        if (!verificationIndex && verificationIndex !== 0) {
            formIsValid = false;
            errors.error = "Please select a verification phone number";
        }
        setErrors(errors);
        return formIsValid;
    }, [verificationIndex]);

    const submitVerification = useCallback(async () => {
        if (showSpinner) {
            return;
        }
        if (validateVerificationIndex()) {
            setErrors({});
            setShowSpinner(true);
            const url = "/verifyAccount";
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        type,
                        channel: channel?.label,
                        methods,
                        challengeTicket,
                        session,
                        index: verificationIndex,
                        credentials,
                        accountID
                    })
                });
                if (response.ok) {
                    const data = await response.json();
                    handleResponse(data);
                } else {
                    const data = await response.json();
                    const errors = {error: data.message};
                    setErrors(errors);
                }
            } catch {
                setErrors({error: "There was an issue verifying the account."});
            }
        }
        setShowSpinner(false);
    }, [
        accountID,
        channel,
        credentials,
        methods,
        challengeTicket,
        session,
        showSpinner,
        type,
        verificationIndex,
        validateVerificationIndex,
        handleResponse
    ]);

    const validateCode = useCallback(() => {
        const errors: Errors = {};
        let formIsValid = true;
        if (!code) {
            formIsValid = false;
            errors.verificationCode = "Please enter the verification code that was sent to you.";
        }
        setErrors(errors);
        return formIsValid;
    }, [code]);

    const submitCode = useCallback(async () => {
        if (showSpinner) {
            return;
        }
        if (validateCode()) {
            setErrors({});
            setShowSpinner(true);
            const url = "/verifyAccount";
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        type,
                        channel: channel?.label,
                        methods,
                        challengeTicket,
                        session,
                        code,
                        credentials,
                        accountID
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    handleResponse(data);
                    await updateUser();
                } else {
                    const data = await response.json();
                    const errors = {error: data.message};
                    setErrors(errors);
                }
            } catch {
                setErrors({error: "There was an issue verifying the account."});
            }
        }
        setShowSpinner(false);
    }, [
        accountID,
        channel,
        code,
        credentials,
        session,
        showSpinner,
        type,
        methods,
        challengeTicket,
        updateUser,
        validateCode,
        handleResponse
    ]);

    const oauthAirbnbUrl = useAirbnbOAuthUrl({
        returnUrl: oAuthReturnUrl,
        state: oAuthStateParameter
    });

    const handleSubmitCredentials = useCallback(async () => {
        if (channel?.value === "Airbnb") {
            setShowSpinner(true);
            window.open(oauthAirbnbUrl, "_self");
        } else {
            submitAddAccount();
        }
    }, [channel?.value, oauthAirbnbUrl, submitAddAccount]);

    const handleSubmit = useCallback(async () => {
        if (type === "credentials") {
            handleSubmitCredentials();
        } else if (type === "verification") {
            submitVerification();
        } else if (type === "code") {
            submitCode();
        }
    }, [type, handleSubmitCredentials, submitVerification, submitCode]);

    return (
        <Modal
            isOpen
            btnTextOk={buttonText}
            isLoading={showSpinner}
            responsive={responsive}
            size={modalSize}
            title={title}
            useDefaultDivider={false}
            onClose={hideCloseButtons ? undefined : onHide}
            onSubmit={type !== "channels" && !isMobile ? handleSubmit : undefined}
        >
            <VStack className="flex-1 px-5 md:px-0">
                {type === "channels" && (
                    <Channels
                        channels={channels}
                        connectedChannels={connectedChannels}
                        disconnectedChannels={disconnectedChannels}
                        hideSocial={hideSocial}
                        onSelectChannel={selectChannel}
                    />
                )}
                {type === "credentials" && channel && credentials && (
                    <Credentials
                        buttonText={buttonText}
                        channel={channel.value}
                        credentials={credentials}
                        errors={errors}
                        info={info}
                        showSpinner={showSpinner}
                        onChange={handleChangeCredentials}
                        onSubmitCredentials={handleSubmit}
                    />
                )}
                {type === "verification" && channel && (
                    <Verification
                        buttonText={buttonText}
                        channel={channel}
                        errors={errors}
                        info={info}
                        methods={methods}
                        onChange={setVerificationIndex}
                        onSubmit={handleSubmit}
                    />
                )}
                {type === "code" && channel && (
                    <Code
                        buttonText={buttonText}
                        channel={channel}
                        errors={errors}
                        method={methods[verificationIndex]?.type}
                        onChange={setCode}
                        onSubmit={handleSubmit}
                    />
                )}
            </VStack>
        </Modal>
    );
};

export default memo(ModalAddAccount);
